import { type ClassValue, clsx } from "clsx";
import { twMerge } from "tailwind-merge";

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

// 统一日期展示（后端返回的是 MySQL DATETIME(3)，带毫秒）
// 空值一律返回空字符串（不是 "未填"，因为 TableCell/Badge 会自己加占位 "—"）
export function formatDate(v: string | Date | null | undefined, withTime = false): string {
  if (!v) return "";
  const d = v instanceof Date ? v : new Date(v);
  if (isNaN(d.getTime())) return String(v);
  const pad = (n: number) => String(n).padStart(2, "0");
  const ymd = `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
  if (!withTime) return ymd;
  return `${ymd} ${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

// 中文格式 YYYY年MM月DD日（对齐 HTML formatDate @946）
// 用于打印 / 导出报告等需要中文语境的地方
export function formatDateCN(v: string | Date | null | undefined): string {
  if (!v) return "未填";
  const d = v instanceof Date ? v : new Date(v);
  if (isNaN(d.getTime())) return String(v);
  return `${d.getFullYear()}年${d.getMonth() + 1}月${d.getDate()}日`;
}

/**
 * 本地日期字符串 YYYY-MM-DD（对齐 HTML getLocalDateString @index.html:755）
 *
 * 为什么需要：Next.js 大量使用 `new Date().toISOString().slice(0, 10)`，
 * toISOString 返回 UTC 日期。对北京用户（UTC+8），本地 00:00-08:00 之间
 * UTC 还停留在前一天 → log_date / 统计查询 / 今日判断全部 off-by-one。
 * 同理 `new Date(y, m, 1).toISOString().slice(0, 10)` 会把本地 4/1 00:00
 * 变成 UTC 3/31 16:00 → 月起点稳定倒推一天。
 *
 * 本函数一律走 getFullYear / getMonth / getDate 本地分量，避免 UTC 漂移。
 */
export function localDateString(d: Date = new Date()): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

// 按姓名哈希出一个稳定的颜色（工程师色块用）
export function nameColor(name: string): string {
  if (!name) return "#64748b";
  let h = 0;
  for (let i = 0; i < name.length; i++) h = (h * 31 + name.charCodeAt(i)) | 0;
  const hue = Math.abs(h) % 360;
  return `hsl(${hue} 60% 50%)`;
}
